// utils/qrPaymentApi.js
// UPI QR payments — the student pays the institute's QR and uploads proof; staff
// approve it in Payment approvals. Backend: vc_be/routes/qrPaymentRoutes.js.
import apiClient from './apiClient';

const QR = '/qr-payments';

/**
 * Whether a course takes QR at enrolment. It FOLLOWS THE INSTITUTE (Settings →
 * Payments) unless the course opted out — so a course with no setting at all
 * is yes. Mirrors vc_be utils/qrPaymentRules.courseAcceptsQr.
 */
export const courseAcceptsQr = (course) => course?.qrPayment?.accept !== false;

// ── Student ──

/** `{ enabled:false }`, or the QR image URL, UPI ID, payee and instructions. */
export const getQrConfig = () => apiClient.get(`${QR}/config`);

/**
 * Send a payment proof for review.
 * @param {object} p
 * @param {'batch_enrollment'|'self_paced_purchase'|'fee_payment'} p.purpose
 * @param {File} p.proof        the screenshot (or bank PDF)
 * @param {string} p.utr        UPI transaction ID
 * @param {string} [p.courseId] enrolments
 * @param {string} [p.batchId]  batch enrolments
 * @param {string} [p.courseAssignmentId] fee payments
 * @param {number} [p.amount]   fee payments — an enrolment's amount is the server's
 */
export const submitQrPayment = ({ proof, ...fields }) => {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") form.append(k, v);
  });
  form.append("proof", proof);
  return apiClient.post(QR, form, { headers: { "Content-Type": "multipart/form-data" } });
};

export const getMyQrPayments = (params = {}) => apiClient.get(`${QR}/my`, { params });

export const cancelMyQrPayment = (id) => apiClient.post(`${QR}/my/${id}/cancel`);

export const downloadMyQrInvoice = (id) => apiClient.get(`${QR}/my/${id}/invoice`, { responseType: "blob" });

// ── Staff ──

export const listQrPayments = (params = {}) => apiClient.get(QR, { params });

export const getQrPayment = (id) => apiClient.get(`${QR}/${id}`);

export const approveQrPayment = (id, body) => apiClient.post(`${QR}/${id}/approve`, body);

export const rejectQrPayment = (id, reason) => apiClient.post(`${QR}/${id}/reject`, { reason });

export const downloadQrInvoice = (id) => apiClient.get(`${QR}/${id}/invoice`, { responseType: "blob" });

// ── Settings ──

export const getQrSettings = () => apiClient.get(`${QR}/settings`);

export const updateQrSettings = (body) => apiClient.put(`${QR}/settings`, body);

export const uploadQrImage = (file) => {
  const form = new FormData();
  form.append("file", file);
  return apiClient.post(`${QR}/settings/qr-image`, form, { headers: { "Content-Type": "multipart/form-data" } });
};

export const removeQrImage = () => apiClient.delete(`${QR}/settings/qr-image`);

/** Save a PDF response as a file. */
export function saveBlob(response, fileName) {
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
